import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from "@apollo/client";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import * as Sentry from "@sentry/core";
import * as ScreenOrientation from "expo-screen-orientation";
import { Fragment, useCallback } from "react";
import { Platform, RefreshControl } from "react-native";
import { SystemBars } from "react-native-edge-to-edge";
import { graphql } from "@oui/lib/src/graphql/tada";
import { ActivityIndicator } from "../components/ActivityIndicator";
import { LearnProgress } from "../components/LearnProgress";
import { ScrollHere, ScrollView } from "../components/ScrollView";
import { SessionCard } from "../components/SessionCard";
import { TabHeader } from "../components/TabHeader";
import { Heading, Text } from "../components/Text";
import { View } from "../components/View";
import { Environment, environment } from "../constants";
import { useI18n } from "../lib/i18n";
export var LearnSessionsFragment = graphql("\n  fragment LearnSessions on Role @_unmask {\n    ID\n    sessions {\n      session {\n        sessionID\n        order\n        name\n        description\n        contentType\n        agenda\n        illustration {\n          fileUploadID\n          gcsPath\n        }\n        completedIllustration {\n          fileUploadID\n          gcsPath\n        }\n        group {\n          groupID\n          name\n        }\n      }\n      status\n      lockedReason\n      unlockedAt\n    }\n  }\n");
export var LearnQuery = graphql("\n    query AppCoreLearn {\n      user {\n        ID\n        role {\n          ID\n          streak # Refetch in case a StreakModal needs to know if this updates after returning from a session\n          ...LearnSessions\n        }\n      }\n    }\n  ", [
    LearnSessionsFragment
]);
/**
 * Loads and displays list of sessions w/statuses from
 * <https://ouihealth.atlassian.net/browse/APICORE-1031> .
 *
 * {@adf 💾̀̀̇̋̀̀̂̂̀̀̇̄̀̀̇̉̀̀̇̀̀̀̆̅̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̍̀̀̆̅̀̀̆̄̀̀̆̉̀̀̆́̀̀̅̃̀̀̆̉̀̀̆̎̀̀̆̇̀̀̆̌̀̀̆̅̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆́̀̀̇̄̀̀̇̄̀̀̇̂̀̀̇̃̀̀̂̂̀̀̃̊̀̀̇̋̀̀̂̂̀̀̆̌̀̀̆́̀̀̇̉̀̀̆̏̀̀̇̅̀̀̇̄̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆́̀̀̆̌̀̀̆̉̀̀̆̇̀̀̆̎̀̀̂̍̀̀̇̃̀̀̇̄̀̀̆́̀̀̇̂̀̀̇̄̀̀̂̂̀̀̇̍̀̀̂̌̀̀̂̂̀̀̆̃̀̀̆̏̀̀̆̎̀̀̇̄̀̀̆̅̀̀̆̎̀̀̇̄̀̀̂̂̀̀̃̊̀̀̅̋̀̀̇̋̀̀̂̂̀̀̇̄̀̀̇̉̀̀̇̀̀̀̆̅̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̍̀̀̆̅̀̀̆̄̀̀̆̉̀̀̆́̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆́̀̀̇̄̀̀̇̄̀̀̇̂̀̀̇̃̀̀̂̂̀̀̃̊̀̀̇̋̀̀̂̂̀̀̇̄̀̀̇̉̀̀̇̀̀̀̆̅̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̆̀̀̆̉̀̀̆̌̀̀̆̅̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆̉̀̀̆̄̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̄̀̀̃̀̀̀̃̇̀̀̃́̀̀̃̃̀̀̆̆̀̀̃̉̀̀̆̄̀̀̂̍̀̀̆̂̀̀̆́̀̀̃̅̀̀̃̅̀̀̂̍̀̀̃̄̀̀̃̃̀̀̃̄̀̀̆̆̀̀̂̍̀̀̆́̀̀̃̆̀̀̆́̀̀̃̄̀̀̂̍̀̀̆̅̀̀̃̇̀̀̆̅̀̀̆̅̀̀̃̀̀̀̃̃̀̀̆̄̀̀̆̆̀̀̃́̀̀̃̄̀̀̃̈̀̀̃̈̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆́̀̀̆̌̀̀̇̄̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̉̀̀̆̍̀̀̆́̀̀̆̇̀̀̆̅̀̀̂̍̀̀̃̂̀̀̃̀̀̀̃̂̀̀̃̄̀̀̃̀̀̀̃̈̀̀̃̀̀̀̃̇̀̀̂̍̀̀̃́̀̀̃̇̀̀̃́̀̀̃́̀̀̃̅̀̀̃̄̀̀̂̎̀̀̇̀̀̀̆̎̀̀̆̇̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆̃̀̀̆̏̀̀̆̌̀̀̆̌̀̀̆̅̀̀̆̃̀̀̇̄̀̀̆̉̀̀̆̏̀̀̆̎̀̀̂̂̀̀̃̊̀̀̂̂̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆̈̀̀̆̅̀̀̆̉̀̀̆̇̀̀̆̈̀̀̇̄̀̀̂̂̀̀̃̊̀̀̃̈̀̀̃̃̀̀̃̉̀̀̂̌̀̀̂̂̀̀̇̇̀̀̆̉̀̀̆̄̀̀̇̄̀̀̆̈̀̀̂̂̀̀̃̊̀̀̃́̀̀̃̉̀̀̃̉̀̀̇̍̀̀̇̍̀̀̅̍̀̀̇̍}
 *
 * Can display `RoleSession.lockedReason` if available
 *
 * {@adf 💾̀̀̇̋̀̀̂̂̀̀̇̄̀̀̇̉̀̀̇̀̀̀̆̅̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̍̀̀̆̅̀̀̆̄̀̀̆̉̀̀̆́̀̀̅̃̀̀̆̉̀̀̆̎̀̀̆̇̀̀̆̌̀̀̆̅̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆́̀̀̇̄̀̀̇̄̀̀̇̂̀̀̇̃̀̀̂̂̀̀̃̊̀̀̇̋̀̀̂̂̀̀̆̌̀̀̆́̀̀̇̉̀̀̆̏̀̀̇̅̀̀̇̄̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆́̀̀̆̌̀̀̆̉̀̀̆̇̀̀̆̎̀̀̂̍̀̀̇̃̀̀̇̄̀̀̆́̀̀̇̂̀̀̇̄̀̀̂̂̀̀̇̍̀̀̂̌̀̀̂̂̀̀̆̃̀̀̆̏̀̀̆̎̀̀̇̄̀̀̆̅̀̀̆̎̀̀̇̄̀̀̂̂̀̀̃̊̀̀̅̋̀̀̇̋̀̀̂̂̀̀̇̄̀̀̇̉̀̀̇̀̀̀̆̅̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̍̀̀̆̅̀̀̆̄̀̀̆̉̀̀̆́̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆́̀̀̇̄̀̀̇̄̀̀̇̂̀̀̇̃̀̀̂̂̀̀̃̊̀̀̇̋̀̀̂̂̀̀̇̄̀̀̇̉̀̀̇̀̀̀̆̅̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̆̀̀̆̉̀̀̆̌̀̀̆̅̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆̉̀̀̆̄̀̀̂̂̀̀̃̊̀̀̂̂̀̀̃̉̀̀̃́̀̀̃̉̀̀̃̅̀̀̃̀̀̀̆̂̀̀̃̃̀̀̃̃̀̀̂̍̀̀̆̄̀̀̃̅̀̀̃̉̀̀̃̇̀̀̂̍̀̀̃̄̀̀̆̅̀̀̃̆̀̀̃̀̀̀̂̍̀̀̆̂̀̀̃̉̀̀̆̄̀̀̃́̀̀̂̍̀̀̆́̀̀̆̃̀̀̃̄̀̀̆̅̀̀̃̉̀̀̃̇̀̀̃̂̀̀̃̆̀̀̃̃̀̀̃̂̀̀̃̄̀̀̃̉̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆́̀̀̆̌̀̀̇̄̀̀̂̂̀̀̃̊̀̀̂̂̀̀̆̉̀̀̆̍̀̀̆́̀̀̆̇̀̀̆̅̀̀̂̍̀̀̃̂̀̀̃̀̀̀̃̂̀̀̃̅̀̀̃̀̀̀̃̅̀̀̃̂̀̀̃̆̀̀̂̍̀̀̃́̀̀̃̉̀̀̃̃̀̀̃̂̀̀̃̄̀̀̃̇̀̀̂̎̀̀̇̀̀̀̆̎̀̀̆̇̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆̃̀̀̆̏̀̀̆̌̀̀̆̌̀̀̆̅̀̀̆̃̀̀̇̄̀̀̆̉̀̀̆̏̀̀̆̎̀̀̂̂̀̀̃̊̀̀̂̂̀̀̂̂̀̀̂̌̀̀̂̂̀̀̆̈̀̀̆̅̀̀̆̉̀̀̆̇̀̀̆̈̀̀̇̄̀̀̂̂̀̀̃̊̀̀̃̄̀̀̃̅̀̀̃̄̀̀̂̌̀̀̂̂̀̀̇̇̀̀̆̉̀̀̆̄̀̀̇̄̀̀̆̈̀̀̂̂̀̀̃̊̀̀̃̂̀̀̃̇̀̀̃̅̀̀̇̍̀̀̇̍̀̀̅̍̀̀̇̍}
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-8}
 */ export function Learn(props) {
    var _data_user_role, _data_user;
    var _useQuery = useQuery(LearnQuery), data = _useQuery.data, refetch = _useQuery.refetch, loading = _useQuery.loading;
    var isFocused = useIsFocused();
    var $t = useI18n().$t;
    useFocusEffect(useCallback(function() {
        if (Platform.OS !== 'web') {
            void ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
        }
        try {
            void refetch();
        } catch (e) {
            // HMR error in dev
            if (environment !== Environment.DEVELOPMENT) {
                Sentry.captureException(e);
            }
        }
        return function() {
            if (Platform.OS !== 'web') {
                void ScreenOrientation.unlockAsync();
            }
        };
    }, [
        refetch
    ]));
    var sessions = data === null || data === void 0 ? void 0 : (_data_user = data.user) === null || _data_user === void 0 ? void 0 : (_data_user_role = _data_user.role) === null || _data_user_role === void 0 ? void 0 : _data_user_role.sessions;
    var numTotal = (sessions === null || sessions === void 0 ? void 0 : sessions.length) || 12;
    var numCompleted = (sessions === null || sessions === void 0 ? void 0 : sessions.filter(function(s) {
        return s.status === 'COMPLETED';
    }).length) || 0;
    var hasSeenActive = false;
    var _props_paddingBottom, _props_title;
    return /*#__PURE__*/ _jsxs(View, {
        style: {
            flex: 1
        },
        children: [
            isFocused ? /*#__PURE__*/ _jsx(SystemBars, {
                style: "dark"
            }) : null,
            /*#__PURE__*/ _jsxs(ScrollView, {
                refreshControl: /*#__PURE__*/ _jsx(RefreshControl, {
                    refreshing: !!(sessions && loading),
                    onRefresh: function() {
                        void refetch();
                    },
                    title: $t({
                        id: 'Learn_refreshIndicator',
                        defaultMessage: 'Update'
                    }),
                    progressViewOffset: 150
                }),
                testID: "Learn_scrollView",
                style: {
                    flex: 1
                },
                contentContainerStyle: {
                    paddingHorizontal: 20,
                    paddingBottom: (_props_paddingBottom = props.paddingBottom) !== null && _props_paddingBottom !== void 0 ? _props_paddingBottom : 20
                },
                children: [
                    /*#__PURE__*/ _jsx(TabHeader, {
                        heading: (_props_title = props.title) !== null && _props_title !== void 0 ? _props_title : $t({
                            id: 'Learn_heading',
                            defaultMessage: 'Learn'
                        })
                    }),
                    /*#__PURE__*/ _jsx(LearnProgress, {
                        backgroundColor: props.progressBackgroundColor,
                        numCompleted: numCompleted,
                        numTotal: numTotal
                    }),
                    /*#__PURE__*/ _jsx(View, {
                        style: {
                            gap: 10,
                            marginTop: 20
                        },
                        children: sessions ? sessions.map(function(session, i, arr) {
                            var _prev_session_group, _item_completedIllustration, _item_illustration, _item_illustration1;
                            var prev = arr[i - 1];
                            var slug = session.session.contentType;
                            var state = session.status;
                            var item = session.session;
                            var details = item.agenda;
                            var newGroup = !!(session.session.group && session.session.group.groupID !== (prev === null || prev === void 0 ? void 0 : (_prev_session_group = prev.session.group) === null || _prev_session_group === void 0 ? void 0 : _prev_session_group.groupID));
                            var unlockedState = hasSeenActive ? 'unlocked' : 'active';
                            var sessionState = {
                                LOCKED: // If unlockedAt exists but is in the past, then this
                                // session was previously locked but is now unlocked. If unlockedAt is null, and
                                // state is locked, then the session is locked by some mechanism other than timestamp
                                (session === null || session === void 0 ? void 0 : session.unlockedAt) && session.unlockedAt < new Date().toISOString() ? unlockedState : 'locked',
                                // If unlocked and no previous session is active then this should show as active
                                UNLOCKED: unlockedState,
                                COMPLETED: 'complete',
                                IN_PROGRESS: 'active'
                            }[state];
                            if (sessionState === 'active') hasSeenActive = true;
                            return /*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    sessionState === 'active' && // don't need to scroll if we're the first session
                                    i !== 0 ? /*#__PURE__*/ _jsx(View, {
                                        style: {
                                            marginTop: 20
                                        },
                                        children: /*#__PURE__*/ _jsx(ScrollHere, {
                                            offsetY: 0
                                        })
                                    }) : null,
                                    newGroup && session.session.group && session.session.group.name !== 'Default' ? /*#__PURE__*/ _jsx(View, {
                                        style: {
                                            marginTop: i === 0 ? undefined : 50
                                        },
                                        children: /*#__PURE__*/ _jsx(Heading, {
                                            text: session.session.group.name,
                                            level: 2
                                        })
                                    }) : null,
                                    session.lockedReason ? /*#__PURE__*/ _jsx(Text, {
                                        size: 15,
                                        text: session.lockedReason,
                                        style: {
                                            marginVertical: 10
                                        }
                                    }) : null,
                                    /*#__PURE__*/ _jsx(SessionCard, {
                                        testID: "SessionCard_".concat(slug),
                                        state: sessionState,
                                        session: {
                                            num: i + 1,
                                            details: details,
                                            title: item.name,
                                            subtitle: item.description,
                                            illustrationUrl: sessionState === 'complete' ? ((_item_completedIllustration = item.completedIllustration) === null || _item_completedIllustration === void 0 ? void 0 : _item_completedIllustration.gcsPath) || ((_item_illustration = item.illustration) === null || _item_illustration === void 0 ? void 0 : _item_illustration.gcsPath) : (_item_illustration1 = item.illustration) === null || _item_illustration1 === void 0 ? void 0 : _item_illustration1.gcsPath
                                        },
                                        unlockedAt: (prev === null || prev === void 0 ? void 0 : prev.status) === 'COMPLETED' ? session.unlockedAt : undefined,
                                        onPressTop: function() {
                                            props.onSessionCardPress(session, i + 1);
                                        }
                                    })
                                ]
                            }, session.session.sessionID);
                        }) : /*#__PURE__*/ _jsx(ActivityIndicator, {})
                    }),
                    props.bottomContent
                ]
            })
        ]
    });
}
